import { round } from '../../../utils/typeUtils'
import { Item } from '../../operations'
import { HoardOrder } from '../state/types'

const QUANTITY_TOLERANCE = 0.000001

// Invoiced units of an item whose price is credited. The unit amount is what the invoice charged above the correct
// price: with VAT and the payment type surcharge
export type HoardPriceCorrectionPart = {
    itemId: string
    quantity: number
    unitAmount: number
}

export type HoardPriceCorrectionPlan = {
    previousPrice: number
    newPrice: number
    // Only a lower price changes what was already delivered: a higher one only applies to the next deliveries
    isDecrease: boolean
    items: Item[]
    deliveredQuantity: number
    invoicedQuantity: number
    creditNoteParts: HoardPriceCorrectionPart[]
}

// What correcting the price of a product in a financial hoard prices list changes:
// - The prices list, so the next deliveries and invoices use the correct price.
// - If the price is lower, the price of the items already delivered: the hoard balance they consumed comes back by
//   itself, and the uninvoiced units get invoiced at the correct price.
// - If the price is lower and some units were invoiced by product, a fiscal credit note for them (not when the hoard
//   was invoiced in full: that invoice was for the value, not for products)
export const planHoardPriceCorrection = (
    hoard: HoardOrder,
    productId: string,
    newPrice: number,
    deliveredByItem: Map<string, number>
): HoardPriceCorrectionPlan => {
    const previousPrice = hoard.pricesList[productId] || 0
    const isDecrease = newPrice < previousPrice
    const productItems = hoard.items.filter(item => item.productId === productId)

    const deliveredQuantity = round(productItems.reduce((sum, item) => sum + (deliveredByItem.get(item.id) || 0), 0), 6)
    const invoicedByItem = new Map(productItems.map(item =>
        [item.id, Math.min(item.invoiceStats?.invoiced || 0, deliveredByItem.get(item.id) || 0)]
    ))
    const invoicedQuantity = round(Array.from(invoicedByItem.values()).reduce((sum, quantity) => sum + quantity, 0), 6)

    const items = isDecrease ?
        hoard.items.map(item => item.productId === productId ? { ...item, price: Math.min(item.price, newPrice) } : item) :
        hoard.items

    const surchargeFactor = 1 + (hoard.paymentTypeYield || 0) / 100
    const creditNoteParts = isDecrease && !hoard.invoiceStats?.fullInvoiceIssued ?
        productItems
            .map(item => ({
                itemId: item.id,
                quantity: invoicedByItem.get(item.id) || 0,
                unitAmount: (previousPrice - newPrice) * surchargeFactor
            }))
            .filter(part => part.quantity > QUANTITY_TOLERANCE) :
        []

    return { previousPrice, newPrice, isDecrease, items, deliveredQuantity, invoicedQuantity, creditNoteParts }
}

export const getHoardPriceCorrectionAmount = (parts: HoardPriceCorrectionPart[]): number =>
    round(parts.reduce((sum, part) => sum + round(part.unitAmount * part.quantity), 0))
